import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Download, Loader2, Search, Building2, Calendar as CalendarIcon } from 'lucide-react';

interface LeadRow {
  id: string;
  created_at: string;
  tenant_id: string;
  tenant_name?: string;
  tenant_subdomain?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  recommended_products?: string[];
  lead_status: 'Open' | 'Closed';
  broker_status: 'Awaiting Broker' | 'Broker Made Contact' | 'Quote Sent to Client' | 'Client Accepted Quote' | 'Client Paid';
  broker_comments?: string | null;
}

export default function AllLeads() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [filterLeadStatus, setFilterLeadStatus] = useState<'All'|'Open'|'Closed'>('All');
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch leads, join tenant and contact
      const { data, error } = await supabase
        .from('leads')
        .select(`
          id, created_at, tenant_id, recommended_products, lead_status, broker_status, broker_comments,
          tenants:tenant_id ( name, subdomain ),
          lead_contacts ( name, email, phone )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped: LeadRow[] = (data || []).map((r: any) => ({
        id: r.id,
        created_at: r.created_at,
        tenant_id: r.tenant_id,
        tenant_name: r.tenants?.name,
        tenant_subdomain: r.tenants?.subdomain,
        contact_name: r.lead_contacts?.[0]?.name,
        contact_email: r.lead_contacts?.[0]?.email,
        contact_phone: r.lead_contacts?.[0]?.phone,
        recommended_products: r.recommended_products || [],
        lead_status: r.lead_status || 'Open',
        broker_status: r.broker_status || 'Awaiting Broker',
        broker_comments: r.broker_comments || null,
      }));

      setRows(mapped);
    } catch (e) {
      console.error('Failed to load leads', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const inRange = (!dateFrom || r.created_at >= dateFrom) && (!dateTo || r.created_at <= `${dateTo}T23:59:59`);
      const term = search.trim().toLowerCase();
      const matches = !term || [
        r.contact_name,
        r.contact_email,
        r.contact_phone,
        r.tenant_name,
        r.tenant_subdomain,
        r.lead_status,
        (r.recommended_products || []).join(', ')
      ].some(v => (v || '').toLowerCase().includes(term));
      const statusOk = filterLeadStatus === 'All' || r.lead_status === filterLeadStatus;
      return inRange && matches && statusOk;
    });
  }, [rows, dateFrom, dateTo, search, filterLeadStatus]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const headers = [
        'Lead ID','Created','Tenant Name','Subdomain','Contact Name','Email','Phone','Recommended Products',
        'Lead Status','Status','Broker Comments'
      ];
      const lines = [headers.join(',')];
      filtered.forEach(r => {
        const row = [
          r.id,
          new Date(r.created_at).toISOString(),
          r.tenant_name || '',
          r.tenant_subdomain || '',
          r.contact_name || '',
          r.contact_email || '',
          r.contact_phone || '',
          (r.recommended_products || []).join('; '),
          r.lead_status,
          r.broker_status,
          r.broker_comments || ''
        ].map(v => `"${String(v).replaceAll('"', '""')}"`).join(',');
        lines.push(row);
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all_tenant_leads_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">All Leads</h1>
        <p className="text-gray-600">Leads across all tenant instances with tenant flags</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" /> All Tenants
          </CardTitle>
          <CardDescription>Filter, search and export leads from every tenant</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <Label>From Date</Label>
              <div className="relative">
                <CalendarIcon className="h-4 w-4 absolute left-2 top-3 text-gray-400" />
                <Input type="date" className="pl-8" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>To Date</Label>
              <div className="relative">
                <CalendarIcon className="h-4 w-4 absolute left-2 top-3 text-gray-400" />
                <Input type="date" className="pl-8" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Lead Status</Label>
              <select className="border rounded px-2 py-2 text-sm w-full" value={filterLeadStatus} onChange={(e) => setFilterLeadStatus(e.target.value as any)}>
                <option value="All">All</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-3 text-gray-400" />
                <Input className="pl-8" placeholder="Contact, email, tenant, product..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          <Separator />

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Tenant</th>
                  <th className="py-2 pr-4">Contact</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Phone</th>
                  <th className="py-2 pr-4">Recommended Products</th>
                  <th className="py-2 pr-4">Lead Status</th>
                  <th className="py-2 pr-4">Broker Comments</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="py-6 text-center text-gray-500">No leads</td></tr>
                ) : (
                  filtered.map(r => (
                    <tr key={r.id} className="border-b align-top">
                      <td className="py-2 pr-4 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <Badge variant="outline">{r.tenant_name || 'Unknown'}</Badge>
                        <span className="ml-2 text-gray-500">({r.tenant_subdomain})</span>
                      </td>
                      <td className="py-2 pr-4">{r.contact_name}</td>
                      <td className="py-2 pr-4">{r.contact_email}</td>
                      <td className="py-2 pr-4">{r.contact_phone}</td>
                      <td className="py-2 pr-4">{(r.recommended_products || []).join(', ')}</td>

                      {/* Lead Status */}
                      <td className="py-2 pr-4">
                        <select
                          className="border rounded px-2 py-1 text-sm"
                          value={r.lead_status}
                          onChange={(e) => {
                            const lead_status = e.target.value as 'Open'|'Closed';
                            setRows(prev => prev.map(row => row.id === r.id ? { ...row, lead_status } : row));
                            setDirty(prev => ({ ...prev, [r.id]: true }));
                          }}
                        >
                          <option>Open</option>
                          <option>Closed</option>
                        </select>
                      </td>

                      {/* Broker Comments */}
                      <td className="py-2 pr-4">
                        <textarea
                          className="border rounded px-2 py-1 text-sm w-[300px] min-h-[38px]"
                          placeholder="give reason if client closed or went elsewhere"
                          value={r.broker_comments || ''}
                          onChange={(e) => {
                            const broker_comments = e.target.value;
                            setRows(prev => prev.map(row => row.id === r.id ? { ...row, broker_comments } : row));
                            setDirty(prev => ({ ...prev, [r.id]: true }));
                          }}
                        />
                      </td>

                      {/* Actions */}
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={savingId === r.id || !dirty[r.id]}
                          onClick={async () => {
                            setSavingId(r.id);
                            try {
                              // enforce comments when closing
                              if (r.lead_status === 'Closed' && (!r.broker_comments || r.broker_comments.trim() === '')) {
                                toast({ title: 'Comments required', description: 'Please add a broker comment before closing the lead.', variant: 'destructive' });
                                return;
                              }
                              const update = { lead_status: r.lead_status, broker_comments: r.broker_comments };
                              const { error } = await supabase.from('leads').update(update).eq('id', r.id);
                              if (error) throw error;
                              setDirty(prev => ({ ...prev, [r.id]: false }));
                              toast({ title: 'Saved', description: 'Lead updated' });
                            } catch (e: any) {
                              toast({ title: 'Update failed', description: e.message || 'Error saving lead', variant: 'destructive' });
                            } finally {
                              setSavingId(null);
                            }
                          }}
                        >
                          {savingId === r.id ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>) : 'Save'}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex justify-end">
            <Button onClick={exportCSV} disabled={exporting || loading}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

